import {
  NOTIFICATION_OPERATOR_RECIPIENTS_MAX,
  type NotificationOperatorRecipient,
} from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { OperatorRecipientForm, OperatorRecipientRow } from '@/features/notification/settings';
import { ContentCard, formStyles } from '@/shared/ui';

interface OperatorRecipientsSectionProps {
  /** 등록 순 운영자 수신자. */
  readonly recipients: readonly NotificationOperatorRecipient[];
  /** 수신자가 비었을 때 쓰는 환경 변수 기본값(QUOTE_NOTIFY_EMAIL)이 api 에 있는가. 주소 자체는 내려오지 않는다. */
  readonly fallbackConfigured: boolean;
}

const DESCRIPTION = `새 견적 요청·주문 취소 요청 메일을 받을 운영자 주소입니다. 한 명만 등록하면 부재 시 놓칠 수 있습니다. 최대 ${NOTIFICATION_OPERATOR_RECIPIENTS_MAX}명.`;
const FALLBACK_IN_USE =
  '등록된 담당자가 없어 기본 주소로 보내고 있습니다. 담당자를 등록하면 등록한 주소로만 보냅니다.';
const FALLBACK_MISSING =
  '등록된 담당자가 없어 운영자 알림을 보내지 않습니다. 발송 이력에는 "운영자 수신자 없음"으로 남습니다. 담당자를 한 명 이상 등록해 주세요.';

/**
 * 알림 받을 담당자(알림 설계 N4·§6-2) — 운영자 메일을 받을 주소. 수신자마다 따로 보내고 따로 기록한다.
 * 비어 있으면 환경 변수 기본값으로 보내는지, 아무에게도 보내지 않는지 안내한다.
 */
export const OperatorRecipientsSection = ({
  recipients,
  fallbackConfigured,
}: OperatorRecipientsSectionProps): ReactElement => (
  <ContentCard title="알림 받을 담당자" description={DESCRIPTION}>
    {recipients.length === 0 ? (
      <p className={fallbackConfigured ? formStyles.hint : formStyles.notice}>
        {fallbackConfigured ? FALLBACK_IN_USE : FALLBACK_MISSING}
      </p>
    ) : (
      <ul
        aria-label="운영자 수신자 목록"
        className="divide-y divide-foreground/[0.06] rounded-xl border border-foreground/[0.06]"
      >
        {recipients.map((recipient) => (
          <OperatorRecipientRow key={recipient.id} recipient={recipient} />
        ))}
      </ul>
    )}
    <div className="mt-5">
      <OperatorRecipientForm isFull={recipients.length >= NOTIFICATION_OPERATOR_RECIPIENTS_MAX} />
    </div>
  </ContentCard>
);
